import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useParams, useNavigate } from "react-router";
import { ArrowLeft, Loader2, Terminal, AlertCircle, ChevronRight, CheckCircle2 } from "lucide-react";
import { useChats, href } from "../util";

type Command = { node: string; name: string };

type CommandResult = {
	status: "loading" | "success" | "error" | "form";
	message?: string;
	form?: any;
	session?: any;
};

const CommandFormView = ({
	form,
	session,
	onBack,
}: {
	form: any;
	session: any;
	onBack: () => void;
}) => {
	const [submitting, setSubmitting] = useState(false);
	const [result, setResult] = useState<string | null>(null);
	const fields: any[] = form?.fields ?? [];

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setSubmitting(true);
		try {
			const data = new FormData(e.currentTarget);
			const next = await session.submit?.(data);
			if (next) {
				setResult("Command completed successfully.");
			} else {
				setResult("Command completed.");
			}
		} catch (err) {
			setResult("Error: " + err);
		} finally {
			setSubmitting(false);
		}
	};

	if (result) {
		return (
			<div className="flex flex-col items-center justify-center gap-4 p-6">
				<CheckCircle2 size={32} className="text-green-400" />
				<p className="text-sm text-white/70 text-center">{result}</p>
				<button
					onClick={onBack}
					className="px-4 py-2 rounded-xl bg-white/10 text-sm text-white hover:bg-white/15 transition-colors"
				>
					Back to commands
				</button>
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit} className="p-4 space-y-4">
			{form.title && (
				<h3 className="text-sm font-medium text-white/80">{String(form.title)}</h3>
			)}
			{form.instructions && (
				<p className="text-xs text-white/50">{String(form.instructions)}</p>
			)}
			{fields.map((field: any) => (
				<div key={field.name} className="space-y-1">
					{field.label && (
						<label className="text-xs text-white/60 block">{field.label}</label>
					)}
					{field.type === "boolean" ? (
						<label className="flex items-center gap-2">
							<input
								type="checkbox"
								name={field.name}
								defaultChecked={field.value === "true" || field.value === "1"}
								className="rounded bg-white/10 border-white/20"
							/>
							<span className="text-sm text-white/80">{field.label || field.name}</span>
						</label>
					) : field.options ? (
						<select
							name={field.name}
							defaultValue={field.value}
							className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/10 text-white text-sm focus:outline-none focus:border-primary transition-all"
						>
							{field.options.map((opt: any) => (
								<option key={opt.value} value={opt.value}>
									{opt.label || opt.value}
								</option>
							))}
						</select>
					) : field.type === "text-multi" ? (
						<textarea
							name={field.name}
							defaultValue={field.value}
							rows={3}
							className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/10 text-white text-sm focus:outline-none focus:border-primary transition-all resize-none"
						/>
					) : (
						<input
							name={field.name}
							type={field.type === "password" ? "password" : "text"}
							defaultValue={field.value}
							placeholder={field.label || field.name}
							required={field.required}
							className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/40 text-sm focus:outline-none focus:border-primary transition-all"
						/>
					)}
				</div>
			))}
			<div className="flex gap-2 pt-2">
				<button
					type="button"
					onClick={onBack}
					className="px-4 py-2 rounded-xl bg-white/10 text-sm text-white hover:bg-white/15 transition-colors"
				>
					Cancel
				</button>
				<button
					type="submit"
					disabled={submitting}
					className="px-4 py-2 rounded-xl bg-primary hover:bg-primary-light text-sm text-white font-medium transition-colors disabled:opacity-50"
				>
					{submitting ? "Submitting..." : "Submit"}
				</button>
			</div>
		</form>
	);
};

const NoteView = ({
	form,
	onBack,
}: {
	form: any;
	onBack: () => void;
}) => {
	const fields: any[] = form?.fields ?? [];

	return (
		<div className="p-4 space-y-4">
			{form.title && (
				<h3 className="text-sm font-medium text-white/80">{String(form.title)}</h3>
			)}
			{form.instructions && (
				<p className="text-xs text-white/50">{String(form.instructions)}</p>
			)}
			{fields.length > 0 && (
				<div className="space-y-2 rounded-xl bg-white/5 p-4">
					{fields.map((field: any, i: number) => (
						<div key={field.name || i} className="flex flex-col gap-0.5">
							{field.label && (
								<span className="text-xs text-white/50">{field.label}</span>
							)}
							<span className="text-sm text-white/90">
								{Array.isArray(field.value)
									? field.value.join(", ")
									: (field.value || "--")}
							</span>
						</div>
					))}
				</div>
			)}
			<button
				onClick={onBack}
				className="px-4 py-2 rounded-xl bg-white/10 text-sm text-white hover:bg-white/15 transition-colors"
			>
				Back to commands
			</button>
		</div>
	);
};

export const AppChat = () => {
	const ctx = useOutletContext<any>();
	const params = useParams();
	const navigate = useNavigate();
	const { client, accountId } = ctx;
	const chatId = params.chatId;

	const allChats = useChats(client);
	const chat = allChats.find((c) => c.chatId === chatId);

	const [commands, setCommands] = useState<Command[] | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [activeCommand, setActiveCommand] = useState<string | null>(null);
	const [commandResult, setCommandResult] = useState<CommandResult | null>(null);

	const name = chat?.getDisplayName() || chatId || "App";

	// Fetch available commands
	useEffect(() => {
		if (!chat) return;
		setLoading(true);
		setError(null);
		setCommands(null);

		const fetchCommands = async () => {
			try {
				const cmds = await (chat as any).getCommands?.();
				if (cmds && Array.isArray(cmds)) {
					setCommands(cmds);
				} else if (cmds) {
					// might be an iterator or other structure
					const arr = Array.from(cmds as Iterable<Command>);
					setCommands(arr);
				} else {
					setCommands([]);
				}
			} catch (err) {
				console.error("[AppChat] Failed to fetch commands:", err);
				setError("Failed to load commands: " + err);
				setCommands([]);
			} finally {
				setLoading(false);
			}
		};

		fetchCommands();
	}, [chat]);

	const executeCommand = useCallback(async (node: string, commandName: string) => {
		if (!chat) return;
		setActiveCommand(node);
		setCommandResult({ status: "loading" });

		try {
			const session = await (chat as any).executeCommand?.(node);
			if (session?.form) {
				const form = session.form;
				const formType = form.type;
				const fields: any[] = form.fields ?? [];
				if (formType === "result" || formType === "note" || (fields.length === 0 && form.title)) {
					setCommandResult({ status: "form", form, session });
				} else if (fields.length > 0) {
					setCommandResult({ status: "form", form, session });
				} else {
					setCommandResult({
						status: "success",
						message: String(form.title || `"${commandName}" executed successfully.`),
					});
				}
			} else if (session) {
				setCommandResult({
					status: "success",
					message: `"${commandName}" executed successfully.`,
				});
			} else {
				setCommandResult({
					status: "success",
					message: `"${commandName}" completed.`,
				});
			}
		} catch (err) {
			console.error("[AppChat] Command execution failed:", err);
			setCommandResult({
				status: "error",
				message: "Command failed: " + err,
			});
		}
	}, [chat]);

	const resetCommand = () => {
		setActiveCommand(null);
		setCommandResult(null);
	};

	const goBack = () => {
		navigate(href("/accounts/:accountId/apps", { accountId }));
	};

	// No chat found
	if (!chat && !loading) {
		return (
			<div className="flex flex-col h-full items-center justify-center text-white/40 gap-3">
				<AlertCircle size={32} />
				<p className="text-sm">App not found: {chatId}</p>
				<button
					onClick={goBack}
					className="px-4 py-2 rounded-xl bg-white/10 text-sm text-white hover:bg-white/15 transition-colors"
				>
					Back to apps
				</button>
			</div>
		);
	}

	const renderCommandResult = () => {
		if (!commandResult) return null;

		if (commandResult.status === "loading") {
			return (
				<div className="flex flex-col items-center justify-center h-64 gap-3 text-white/40">
					<Loader2 size={24} className="animate-spin" />
					<p className="text-sm">Executing command...</p>
				</div>
			);
		}

		if (commandResult.status === "success") {
			return (
				<div className="flex flex-col items-center justify-center gap-4 p-6">
					<CheckCircle2 size={32} className="text-green-400" />
					<p className="text-sm text-white/70 text-center">{commandResult.message}</p>
					<button
						onClick={resetCommand}
						className="px-4 py-2 rounded-xl bg-white/10 text-sm text-white hover:bg-white/15 transition-colors"
					>
						Back to commands
					</button>
				</div>
			);
		}

		if (commandResult.status === "error") {
			return (
				<div className="flex flex-col items-center justify-center gap-4 p-6">
					<AlertCircle size={32} className="text-red-400" />
					<p className="text-sm text-red-300 text-center">{commandResult.message}</p>
					<button
						onClick={resetCommand}
						className="px-4 py-2 rounded-xl bg-white/10 text-sm text-white hover:bg-white/15 transition-colors"
					>
						Back to commands
					</button>
				</div>
			);
		}

		if (commandResult.status === "form" && commandResult.form && commandResult.session) {
			const formType = commandResult.form.type;
			if (formType === "result" || formType === "note") {
				return <NoteView form={commandResult.form} onBack={resetCommand} />;
			}
			return (
				<CommandFormView
					form={commandResult.form}
					session={commandResult.session}
					onBack={resetCommand}
				/>
			);
		}

		return null;
	};

	return (
		<div className="flex flex-col h-full">
			{/* Header */}
			<div className="flex items-center gap-3 px-4 py-3 border-b border-white/8 shrink-0">
				<button
					onClick={goBack}
					className="md:hidden w-8 h-8 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
				>
					<ArrowLeft size={18} />
				</button>
				<div className="w-9 h-9 rounded-full bg-primary/50 flex items-center justify-center text-white text-xs font-medium shrink-0">
					{name
						.split(" ")
						.map((w: string) => w[0])
						.join("")
						.slice(0, 2)
						.toUpperCase()}
				</div>
				<div className="flex-1 min-w-0">
					<span className="text-sm font-medium text-white/90 truncate block">{name}</span>
					<span className="text-xs text-white/40 truncate block">{chatId}</span>
				</div>
			</div>

			{/* Content */}
			<div className="flex-1 overflow-y-auto">
				{loading ? (
					<div className="flex flex-col items-center justify-center h-full gap-3 text-white/40">
						<Loader2 size={24} className="animate-spin" />
						<p className="text-sm">Loading commands...</p>
					</div>
				) : error && !commands?.length ? (
					<div className="flex flex-col items-center justify-center h-full gap-3 text-white/40">
						<AlertCircle size={24} />
						<p className="text-sm text-center px-6">{error}</p>
					</div>
				) : activeCommand && commandResult ? (
					<div>{renderCommandResult()}</div>
				) : (
					/* Commands list */
					<div className="py-2">
						{commands && commands.length > 0 ? (
							commands.map((cmd) => (
								<button
									key={cmd.node}
									onClick={() => executeCommand(cmd.node, cmd.name)}
									className="w-full text-left px-5 py-3 hover:bg-white/5 transition-colors flex items-center gap-3"
								>
									<Terminal size={16} className="text-white/40 shrink-0" />
									<span className="text-sm text-white flex-1">{cmd.name}</span>
									<ChevronRight size={14} className="text-white/30 shrink-0" />
								</button>
							))
						) : (
							<div className="flex flex-col items-center justify-center h-64 gap-3 text-white/40">
								<Terminal size={32} strokeWidth={1} />
								<p className="text-sm">No commands available</p>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};
